import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  X, Plus, Search, Clock, Heart, Sparkles, Sticker, Layout, Type,
  Scissors, Image as ImageIcon, Tag, Pencil, Trash2, ChevronLeft,
  Star, Moon, TrendingUp, Frame, BookHeart, PencilRuler,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== CATEGORY CONFIG ==============
const CATEGORIES = [
  { key: 'collections',   label: 'Samlingar',         emoji: '📚', icon: BookHeart,    color: '#FFD6A5' },
  { key: 'stickers',      label: 'Klistermärken',     emoji: '✨', icon: Sticker,      color: '#FFC8DD' },
  { key: 'journal_cards', label: 'Journalkort',       emoji: '🗂️', icon: Layout,       color: '#A2D2FF' },
  { key: 'lettering',     label: 'Bokstäver',         emoji: '✍️', icon: PencilRuler,  color: '#CDB4DB' },
  { key: 'text_boxes',    label: 'Textrutor',         emoji: '💬', icon: Type,         color: '#B9FBC0' },
  { key: 'washi',         label: 'Washi-tejp',        emoji: '🎀', icon: Scissors,     color: '#FFB4A2' },
  { key: 'planner_icons', label: 'Planerar-ikoner',   emoji: '📅', icon: Star,         color: '#FFE8A3' },
  { key: 'frames',        label: 'Ramar',             emoji: '🖼️', icon: Frame,        color: '#E0BBE4' },
];

// ============== CURATED KAWAII CONTENT ==============
// These are baked-in (always available) so the Studio looks rich from day one.
const CURATED = {
  stickers: {
    trending: [
      { id: 'cur-st-1', emoji: '🌸', title: 'Körsbärsblom' },
      { id: 'cur-st-2', emoji: '☕', title: 'Mysig fika' },
      { id: 'cur-st-3', emoji: '🦊', title: 'Liten räv' },
      { id: 'cur-st-4', emoji: '🌙', title: 'Måne' },
      { id: 'cur-st-5', emoji: '🍓', title: 'Smultron' },
      { id: 'cur-st-6', emoji: '🧚', title: 'Fé' },
      { id: 'cur-st-7', emoji: '🪐', title: 'Planet' },
      { id: 'cur-st-8', emoji: '🌈', title: 'Regnbåge' },
    ],
    dark: [
      { id: 'cur-st-d1', emoji: '🌑', title: 'Nymåne', dark: true },
      { id: 'cur-st-d2', emoji: '🦇', title: 'Fladdermus', dark: true },
      { id: 'cur-st-d3', emoji: '🕯️', title: 'Ljus', dark: true },
      { id: 'cur-st-d4', emoji: '🔮', title: 'Spåkula', dark: true },
      { id: 'cur-st-d5', emoji: '⚡', title: 'Blixt', dark: true },
      { id: 'cur-st-d6', emoji: '🖤', title: 'Svart hjärta', dark: true },
    ],
    recent: [
      { id: 'cur-st-r1', emoji: '🐱', title: 'Kattunge' },
      { id: 'cur-st-r2', emoji: '🍡', title: 'Dango' },
      { id: 'cur-st-r3', emoji: '🌷', title: 'Tulpan' },
      { id: 'cur-st-r4', emoji: '🧋', title: 'Bubble tea' },
    ],
  },
  washi: {
    trending: [
      { id: 'cur-w-1', title: 'Rosa prickar', pattern: 'repeating-linear-gradient(90deg,#FFC8DD 0,#FFC8DD 8px,#FFE0EE 8px,#FFE0EE 16px)' },
      { id: 'cur-w-2', title: 'Mintränder', pattern: 'repeating-linear-gradient(45deg,#B9FBC0 0,#B9FBC0 6px,#D5FDD8 6px,#D5FDD8 12px)' },
      { id: 'cur-w-3', title: 'Regnbåge',  pattern: 'linear-gradient(90deg,#FF6B6B,#FFC8DD,#FDF0D5,#B9FBC0,#A2D2FF,#CDB4DB)' },
      { id: 'cur-w-4', title: 'Lila våg',  pattern: 'repeating-linear-gradient(135deg,#CDB4DB 0,#CDB4DB 6px,#E0D0E8 6px,#E0D0E8 12px)' },
    ],
    dark: [
      { id: 'cur-w-d1', title: 'Midnattsblå', pattern: 'repeating-linear-gradient(90deg,#1E1B3A 0,#1E1B3A 8px,#2E2A5A 8px,#2E2A5A 16px)', dark: true },
      { id: 'cur-w-d2', title: 'Svart spets',  pattern: 'repeating-linear-gradient(45deg,#0A0A1A 0,#0A0A1A 4px,#2A1A3A 4px,#2A1A3A 8px)', dark: true },
    ],
    recent: [
      { id: 'cur-w-r1', title: 'Korall',      pattern: 'repeating-linear-gradient(0deg,#FF6B6B 0,#FF6B6B 4px,#FFB0B0 4px,#FFB0B0 8px)' },
      { id: 'cur-w-r2', title: 'Solgul',      pattern: 'linear-gradient(90deg,#FDF0D5,#F5E6C0)' },
    ],
  },
  frames: {
    trending: [
      { id: 'cur-f-1', title: 'Rosa hjärta',  color: '#FFC8DD', emoji: '💖' },
      { id: 'cur-f-2', title: 'Mintram',      color: '#B9FBC0', emoji: '🌿' },
      { id: 'cur-f-3', title: 'Lavendel',     color: '#CDB4DB', emoji: '🌸' },
      { id: 'cur-f-4', title: 'Solguld',      color: '#FFE8A3', emoji: '🌟' },
    ],
    dark: [
      { id: 'cur-f-d1', title: 'Galaktisk',  color: '#2E2A5A', emoji: '🪐', dark: true },
      { id: 'cur-f-d2', title: 'Midnatt',    color: '#0A0A1A', emoji: '🌙', dark: true },
    ],
    recent: [
      { id: 'cur-f-r1', title: 'Korall',     color: '#FFB4A2', emoji: '🌺' },
    ],
  },
  text_boxes: {
    trending: [
      { id: 'cur-tb-1', title: 'Handskriven', style: 'handwritten', text: 'Skriv något fint…' },
      { id: 'cur-tb-2', title: 'Rosa post-it', style: 'sticky_pink', text: 'Kom ihåg ♡' },
      { id: 'cur-tb-3', title: 'Skrivmaskin', style: 'typewriter', text: 'Dagens tanke' },
      { id: 'cur-tb-4', title: 'Citat',        style: 'quote', text: '"Små steg räknas"' },
    ],
    dark: [
      { id: 'cur-tb-d1', title: 'Mörk etikett', style: 'dark_label', text: 'NATTANTECKNING', dark: true },
    ],
    recent: [
      { id: 'cur-tb-r1', title: 'Mint post-it', style: 'sticky_green', text: 'Idag är jag tacksam för…' },
    ],
  },
  journal_cards: {
    trending: [
      { id: 'cur-jc-1', title: 'Dagens 3 mål', emoji: '🎯', color: '#A2D2FF', text: '1. \n2. \n3. ' },
      { id: 'cur-jc-2', title: 'Tacksamhet',  emoji: '🙏', color: '#B9FBC0', text: 'Idag är jag tacksam för…' },
      { id: 'cur-jc-3', title: 'Humör',       emoji: '🌈', color: '#FFC8DD', text: 'Hur mår jag idag?' },
      { id: 'cur-jc-4', title: 'Veckans vins', emoji: '🏆', color: '#FFE8A3', text: 'Min vinst denna vecka…' },
    ],
    dark: [
      { id: 'cur-jc-d1', title: 'Drömjournal', emoji: '🌙', color: '#2E2A5A', text: 'Drömmen jag minns…', dark: true },
    ],
    recent: [
      { id: 'cur-jc-r1', title: 'Self-care', emoji: '🛁', color: '#CDB4DB', text: 'Idag tog jag hand om mig genom att…' },
    ],
  },
  lettering: {
    trending: [
      { id: 'cur-le-1', title: 'Bubbelstil',  text: 'Hej!',     style: 'bubble' },
      { id: 'cur-le-2', title: 'Kursiv',      text: 'kawaii',   style: 'cursive' },
      { id: 'cur-le-3', title: 'Vintage',     text: 'BuJo',     style: 'vintage' },
      { id: 'cur-le-4', title: 'Pastell',     text: 'sweet',    style: 'pastel' },
    ],
    dark: [
      { id: 'cur-le-d1', title: 'Neon',       text: 'MAGIC',    style: 'neon', dark: true },
    ],
    recent: [
      { id: 'cur-le-r1', title: 'Handskriven', text: 'mys',     style: 'hand' },
    ],
  },
  planner_icons: {
    trending: [
      { id: 'cur-pi-1', emoji: '📌', title: 'Pin' },
      { id: 'cur-pi-2', emoji: '✏️', title: 'Penna' },
      { id: 'cur-pi-3', emoji: '🔖', title: 'Bokmärke' },
      { id: 'cur-pi-4', emoji: '📎', title: 'Gem' },
      { id: 'cur-pi-5', emoji: '🗓️', title: 'Kalender' },
      { id: 'cur-pi-6', emoji: '⭐', title: 'Stjärna' },
      { id: 'cur-pi-7', emoji: '✅', title: 'Klart' },
      { id: 'cur-pi-8', emoji: '🔔', title: 'Påminnelse' },
    ],
    dark: [
      { id: 'cur-pi-d1', emoji: '🕯️', title: 'Stearin', dark: true },
      { id: 'cur-pi-d2', emoji: '🔮', title: 'Magi', dark: true },
    ],
    recent: [
      { id: 'cur-pi-r1', emoji: '💌', title: 'Brev' },
      { id: 'cur-pi-r2', emoji: '🎀', title: 'Rosett' },
    ],
  },
  collections: {
    trending: [
      { id: 'cur-co-1', title: 'BuJo-startpaket', emoji: '📔', color: '#FFC8DD' },
      { id: 'cur-co-2', title: 'Morgonrutin',     emoji: '☀️', color: '#FFE8A3' },
      { id: 'cur-co-3', title: 'Tacksamhet',      emoji: '💗', color: '#B9FBC0' },
      { id: 'cur-co-4', title: 'Studieplan',      emoji: '📚', color: '#A2D2FF' },
    ],
    dark: [
      { id: 'cur-co-d1', title: 'Drömpaket',      emoji: '🌙', color: '#2E2A5A', dark: true },
    ],
    recent: [
      { id: 'cur-co-r1', title: 'Veckoöversikt',  emoji: '📅', color: '#CDB4DB' },
    ],
  },
};

const TEXT_BOX_STYLE = {
  handwritten: { font: "'Fredoka', sans-serif", bg: 'transparent', border: '2px dashed #CDB4DB', color: '#1C1C1E' },
  typewriter:  { font: "'Courier New', monospace", bg: '#FDF0D5', border: 'none', color: '#333' },
  sticky_pink: { font: "'Nunito', sans-serif", bg: '#FFC8DD', border: 'none', color: '#1C1C1E', shadow: '3px 3px 0px rgba(0,0,0,0.15)' },
  sticky_green:{ font: "'Nunito', sans-serif", bg: '#B9FBC0', border: 'none', color: '#1C1C1E', shadow: '3px 3px 0px rgba(0,0,0,0.15)' },
  quote:       { font: "'Georgia', serif", bg: '#FFFAF0', border: '4px solid #CDB4DB', borderLeft: '4px solid #CDB4DB', color: '#555' },
  dark_label:  { font: "'Fredoka', sans-serif", bg: '#1E1B3A', border: '2px solid #FFC8DD', color: '#FFC8DD' },
};

const LETTERING_STYLE = {
  bubble:  { fontFamily: "'Fredoka', sans-serif", fontWeight: 700, fontSize: 28, color: '#FF6B9D', textShadow: '2px 2px 0 #fff, 3px 3px 0 #000' },
  cursive: { fontFamily: "'Brush Script MT', cursive", fontSize: 32, color: '#A2D2FF' },
  vintage: { fontFamily: "'Georgia', serif", fontSize: 26, color: '#7A5A3A', textShadow: '1px 1px 0 #FDF0D5' },
  pastel:  { fontFamily: "'Fredoka', sans-serif", fontSize: 26, color: '#CDB4DB' },
  neon:    { fontFamily: "'Fredoka', sans-serif", fontSize: 28, color: '#FF6B9D', textShadow: '0 0 8px #FF6B9D, 0 0 16px #FF6B9D' },
  hand:    { fontFamily: "'Caveat', cursive", fontSize: 30, color: '#1C1C1E' },
};

// ============== TINY UI: COPY TOAST ==============
function CopyToast({ message }) {
  if (!message) return null;
  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] neo-card px-4 py-2 bg-[#B9FBC0] font-bold text-sm"
      data-testid="studio-copy-toast"
    >
      Kopierat: {message} 🌸
    </div>
  );
}

// ============== ASSET TILE ==============
function AssetTile({ asset, onClick, onDelete, isOwn }) {
  const dark = asset.dark;
  const style = asset.style && TEXT_BOX_STYLE[asset.style];
  const lettering = asset.style && LETTERING_STYLE[asset.style];

  return (
    <div
      className={`relative group rounded-2xl border-2 border-black overflow-hidden shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] cursor-pointer transition-transform hover:scale-[1.03] active:scale-95 ${
        dark ? 'bg-[#1E1B3A] text-white' : 'bg-white'
      }`}
      onClick={() => onClick?.(asset)}
      data-testid={`studio-asset-${asset.id}`}
      title={asset.title || ''}
    >
      <div className="aspect-square flex items-center justify-center p-3 text-center" style={
        asset.pattern ? { background: asset.pattern } :
        asset.image ? { backgroundImage: `url(${asset.image})`, backgroundSize: 'cover', backgroundPosition: 'center' } :
        (asset.category === 'frames' && asset.color) ? { background: asset.color, padding: 12 } : {}
      }>
        {asset.category === 'frames' && (
          <div className="w-full h-full rounded-xl bg-white/95 border-2 border-dashed flex items-center justify-center text-3xl" style={{ borderColor: asset.color || '#000' }}>
            {asset.emoji || '🖼️'}
          </div>
        )}
        {asset.category === 'text_boxes' && style && (
          <div
            className="w-full h-full rounded-md flex items-center justify-center text-xs p-2 break-words"
            style={{ ...style, boxShadow: style.shadow, borderLeft: style.borderLeft }}
          >
            {asset.text || asset.title}
          </div>
        )}
        {asset.category === 'lettering' && lettering && (
          <span style={lettering}>{asset.text || 'abc'}</span>
        )}
        {asset.category === 'journal_cards' && (
          <div className="w-full h-full rounded-xl flex flex-col items-center justify-center text-xs p-2" style={{ background: asset.color || '#FFC8DD' }}>
            <span className="text-2xl mb-1">{asset.emoji || '📝'}</span>
            <span className="font-bold leading-tight">{asset.title}</span>
          </div>
        )}
        {asset.category === 'collections' && (
          <div className="w-full h-full rounded-xl flex flex-col items-center justify-center" style={{ background: asset.color || '#FFC8DD' }}>
            <span className="text-4xl">{asset.emoji || '📚'}</span>
          </div>
        )}
        {(asset.category === 'stickers' || asset.category === 'planner_icons') && !asset.image && (
          <span className="text-4xl select-none">{asset.emoji || '✨'}</span>
        )}
        {/* Washi pattern shown by background only */}
      </div>
      <div className={`px-2 py-1.5 text-[11px] font-bold text-center truncate ${dark ? 'bg-black/40' : 'bg-white/90 border-t border-black/10'}`}>
        {asset.title || '—'}
      </div>
      {isOwn && onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(asset); }}
          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white border-2 border-black flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          data-testid={`studio-asset-delete-${asset.id}`}
          title="Ta bort"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// ============== ADD ASSET MODAL ==============
function AddAssetModal({ open, category, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [emoji, setEmoji] = useState('');
  const [text, setText] = useState('');
  const [color, setColor] = useState('#FFC8DD');
  const [image, setImage] = useState('');
  const [dark, setDark] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTitle(''); setEmoji(''); setText(''); setColor('#FFC8DD'); setImage(''); setDark(false);
    }
  }, [open]);

  if (!open || !category) return null;
  const catCfg = CATEGORIES.find(c => c.key === category);

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > 2_500_000) {
      window.alert('Bilden är för stor (max ~2MB)');
      return;
    }
    const r = new FileReader();
    r.onload = () => setImage(r.result);
    r.readAsDataURL(file);
  };

  const wantsImage = ['stickers', 'frames', 'planner_icons', 'collections', 'journal_cards'].includes(category);
  const wantsEmoji = ['stickers', 'planner_icons', 'journal_cards', 'collections', 'frames'].includes(category);
  const wantsText  = ['text_boxes', 'lettering', 'journal_cards'].includes(category);
  const wantsColor = ['frames', 'journal_cards', 'collections'].includes(category);

  const save = async () => {
    if (saving) return;
    if (!image && !emoji && !text && !color) {
      window.alert('Lägg till bild, emoji, text eller färg');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/studio/assets`, {
        category, title, emoji, text, color: wantsColor ? color : null, image, dark,
      }, { withCredentials: true });
      onCreated?.(data.asset);
      onClose?.();
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Något gick fel');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[55] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-[#1E1B3A] dark:text-white border-2 border-black rounded-3xl shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="studio-add-modal"
      >
        <div className="p-4 border-b-2 border-black flex items-center justify-between" style={{ background: catCfg?.color }}>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{catCfg?.emoji}</span>
            <h3 className="font-bold font-['Fredoka'] text-lg">Lägg till i {catCfg?.label}</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white border-2 border-black flex items-center justify-center" data-testid="studio-add-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-xs font-bold opacity-70">Titel</span>
            <input
              type="text" value={title} onChange={(e) => setTitle(e.target.value)}
              className="neo-input w-full mt-1" placeholder="t.ex. Min rosa stjärna"
              data-testid="studio-add-title"
            />
          </label>

          {wantsEmoji && (
            <label className="block">
              <span className="text-xs font-bold opacity-70">Emoji (snabb)</span>
              <input
                type="text" value={emoji} onChange={(e) => setEmoji(e.target.value.slice(0, 4))}
                className="neo-input w-full mt-1 text-2xl text-center" placeholder="🌸"
                data-testid="studio-add-emoji"
              />
            </label>
          )}

          {wantsText && (
            <label className="block">
              <span className="text-xs font-bold opacity-70">Text</span>
              <textarea
                value={text} onChange={(e) => setText(e.target.value)} rows={3}
                className="neo-input w-full mt-1" placeholder="Skriv något fint…"
                data-testid="studio-add-text"
              />
            </label>
          )}

          {wantsColor && (
            <label className="flex items-center gap-3">
              <span className="text-xs font-bold opacity-70">Färg</span>
              <input
                type="color" value={color} onChange={(e) => setColor(e.target.value)}
                className="w-12 h-10 rounded-lg border-2 border-black cursor-pointer"
                data-testid="studio-add-color"
              />
              <span className="text-xs opacity-60">{color}</span>
            </label>
          )}

          {wantsImage && (
            <div>
              <span className="text-xs font-bold opacity-70 block mb-1">Bild (valfritt)</span>
              <input
                ref={fileRef} type="file" accept="image/*"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="text-sm"
                data-testid="studio-add-image"
              />
              {image && (
                <div className="mt-2 w-24 h-24 rounded-xl border-2 border-black overflow-hidden">
                  <img src={image} alt="preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
          )}

          <label className="flex items-center gap-2">
            <input type="checkbox" checked={dark} onChange={(e) => setDark(e.target.checked)} data-testid="studio-add-dark" />
            <span className="text-sm flex items-center gap-1"><Moon className="w-4 h-4" /> Mörkt läge</span>
          </label>
        </div>

        <div className="p-4 border-t-2 border-black flex gap-2 justify-end">
          <button onClick={onClose} className="neo-btn px-4 py-2 bg-white" data-testid="studio-add-cancel">Avbryt</button>
          <button onClick={save} disabled={saving} className="neo-btn px-4 py-2 bg-[#FFC8DD] font-bold" data-testid="studio-add-save">
            {saving ? 'Sparar…' : 'Spara 💖'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============== MAIN STUDIO TAB ==============
export default function StudioTab() {
  const [activeCategory, setActiveCategory] = useState(null); // null = overview grid
  const [assets, setAssets] = useState([]); // user assets (and curated)
  const [search, setSearch] = useState('');
  const [counts, setCounts] = useState({}); // category -> count
  const [copyMessage, setCopyMessage] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addCategory, setAddCategory] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/studio/assets`, { withCredentials: true });
      setAssets(data.assets || []);
      const map = {};
      (data.assets || []).forEach(a => { map[a.category] = (map[a.category] || 0) + 1; });
      setCounts(map);
    } catch (e) {
      // silent — Studio is non-critical, render curated content regardless
      setAssets([]);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const onCreated = (asset) => {
    setAssets((prev) => [asset, ...prev]);
    setCounts((c) => ({ ...c, [asset.category]: (c[asset.category] || 0) + 1 }));
  };

  const onDelete = async (asset) => {
    if (!window.confirm(`Ta bort "${asset.title || 'detta'}"?`)) return;
    try {
      await axios.delete(`${API}/studio/assets/${asset.id}`, { withCredentials: true });
      setAssets((prev) => prev.filter(a => a.id !== asset.id));
      setCounts((c) => ({ ...c, [asset.category]: Math.max(0, (c[asset.category] || 1) - 1) }));
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Kunde inte ta bort');
    }
  };

  const onTileClick = (asset) => {
    // Copy emoji/text/title to clipboard for quick use elsewhere
    const text = asset.emoji || asset.text || asset.title || '';
    if (text && navigator.clipboard) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
    setCopyMessage(asset.title || asset.emoji || 'tillgång');
    setTimeout(() => setCopyMessage(''), 1500);
  };

  // ============== OVERVIEW GRID (entry screen) ==============
  if (!activeCategory) {
    const grandTotal = Object.values(counts).reduce((a, b) => a + b, 0);
    return (
      <div className="space-y-5" data-testid="studio-tab">
        <CopyToast message={copyMessage} />

        {/* Header */}
        <div className="neo-card p-5 bg-gradient-to-br from-[#FFF0F8] via-[#F0F5FF] to-[#F8F0FF] dark:from-[#2A1B3A] dark:via-[#1B2A4A] dark:to-[#3A1B3A]">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-2xl font-bold font-['Fredoka'] flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-[#D06892]" /> Studio
              </h2>
              <p className="text-sm opacity-70 mt-1">
                Din kawaii-låda för klistermärken, washi, journalkort & ramar 💖
              </p>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider opacity-60">Dina tillgångar</div>
              <div className="text-2xl font-bold font-['Fredoka']">{grandTotal}</div>
            </div>
          </div>
        </div>

        {/* Category grid (matches user screenshot 1) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="studio-categories">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const count = counts[c.key] || 0;
            return (
              <button
                key={c.key}
                onClick={() => setActiveCategory(c.key)}
                className="neo-card p-4 text-left hover:scale-[1.03] active:scale-95 transition-transform"
                style={{ background: c.color }}
                data-testid={`studio-cat-${c.key}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <Icon className="w-6 h-6" />
                  <span className="text-2xl">{c.emoji}</span>
                </div>
                <div className="font-bold font-['Fredoka'] text-base leading-tight">{c.label}</div>
                <div className="text-[11px] opacity-70 mt-1">
                  {count > 0 ? `${count} egna + kuraterade` : 'Kuraterat innehåll'}
                </div>
              </button>
            );
          })}
        </div>

        {/* Featured sections — Trendar nu */}
        <FeaturedSection
          title="Trendar nu" icon={<TrendingUp className="w-5 h-5" />}
          accent="#FFC8DD"
          getItems={(cat) => CURATED[cat]?.trending || []}
          onTileClick={onTileClick}
          dataTestId="studio-section-trending"
        />

        {/* Mörkt läge */}
        <FeaturedSection
          title="Mörkt läge" icon={<Moon className="w-5 h-5" />}
          accent="#2E2A5A" darkBg
          getItems={(cat) => CURATED[cat]?.dark || []}
          onTileClick={onTileClick}
          dataTestId="studio-section-dark"
        />

        {/* Nyligen tillagt */}
        <FeaturedSection
          title="Nyligen tillagt" icon={<Clock className="w-5 h-5" />}
          accent="#B9FBC0"
          // Mix: user's most recent items + curated 'recent'
          getItems={(cat) => {
            const userRecent = assets.filter(a => a.category === cat).slice(0, 4);
            const curated = CURATED[cat]?.recent || [];
            return [...userRecent.map(a => ({ ...a, _isOwn: true })), ...curated];
          }}
          onTileClick={onTileClick}
          onDelete={onDelete}
          dataTestId="studio-section-recent"
        />
      </div>
    );
  }

  // ============== CATEGORY DETAIL ==============
  const catCfg = CATEGORIES.find(c => c.key === activeCategory);
  const userItems = assets.filter(a => a.category === activeCategory);
  const curatedItems = [
    ...(CURATED[activeCategory]?.trending || []),
    ...(CURATED[activeCategory]?.dark || []),
    ...(CURATED[activeCategory]?.recent || []),
  ].map(it => ({ ...it, category: activeCategory }));

  const q = search.trim().toLowerCase();
  const filterFn = (a) => !q || (a.title || '').toLowerCase().includes(q) || (a.emoji || '').includes(q) || (a.text || '').toLowerCase().includes(q);
  const filteredUser = userItems.filter(filterFn);
  const filteredCurated = curatedItems.filter(filterFn);

  return (
    <div className="space-y-4" data-testid={`studio-detail-${activeCategory}`}>
      <CopyToast message={copyMessage} />

      {/* Header bar */}
      <div className="neo-card p-3 flex items-center justify-between gap-2" style={{ background: catCfg?.color }}>
        <button
          onClick={() => { setActiveCategory(null); setSearch(''); }}
          className="neo-btn px-3 py-2 bg-white flex items-center gap-1 text-sm font-bold"
          data-testid="studio-back"
        >
          <ChevronLeft className="w-4 h-4" /> Tillbaka
        </button>
        <div className="flex items-center gap-2">
          <span className="text-2xl">{catCfg?.emoji}</span>
          <h3 className="font-bold font-['Fredoka'] text-lg">{catCfg?.label}</h3>
        </div>
        <button
          onClick={() => { setAddCategory(activeCategory); setAddOpen(true); }}
          className="neo-btn px-3 py-2 bg-white flex items-center gap-1 text-sm font-bold"
          data-testid="studio-add-open"
        >
          <Plus className="w-4 h-4" /> Lägg till
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          className="neo-input w-full pl-10" placeholder={`Sök i ${catCfg?.label}…`}
          data-testid="studio-search"
        />
      </div>

      {/* User items */}
      {filteredUser.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-2">
            <Heart className="w-4 h-4 text-[#D06892]" />
            <h4 className="font-bold font-['Fredoka']">Mina</h4>
            <span className="text-xs opacity-60">({filteredUser.length})</span>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {filteredUser.map((a) => (
              <AssetTile key={a.id} asset={a} onClick={onTileClick} onDelete={onDelete} isOwn />
            ))}
          </div>
        </section>
      )}

      {/* Curated items */}
      {filteredCurated.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-[#D06892]" />
            <h4 className="font-bold font-['Fredoka']">Kuraterat</h4>
            <span className="text-xs opacity-60">({filteredCurated.length})</span>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {filteredCurated.map((a) => (
              <AssetTile key={a.id} asset={a} onClick={onTileClick} />
            ))}
          </div>
        </section>
      )}

      {filteredUser.length === 0 && filteredCurated.length === 0 && (
        <div className="neo-card p-8 text-center opacity-70">
          <p className="text-sm">Inga träffar för "{search}"</p>
        </div>
      )}

      <AddAssetModal
        open={addOpen}
        category={addCategory}
        onClose={() => setAddOpen(false)}
        onCreated={onCreated}
      />
    </div>
  );
}

// ============== FEATURED SECTION (horizontal scroller) ==============
function FeaturedSection({ title, icon, accent, darkBg, getItems, onTileClick, onDelete, dataTestId }) {
  // Show 2 categories per row (stickers + frames default) — actually flatten across all cats
  const items = CATEGORIES.flatMap((c) => getItems(c.key).map((it) => ({ ...it, category: c.key })));
  if (items.length === 0) return null;

  return (
    <section
      className={`neo-card p-4 ${darkBg ? 'bg-[#1E1B3A]' : 'bg-white'}`}
      style={darkBg ? { color: '#fff' } : undefined}
      data-testid={dataTestId}
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-full flex items-center justify-center border-2 border-black" style={{ background: accent, color: darkBg ? '#fff' : undefined }}>
          {icon}
        </div>
        <h3 className="text-lg font-bold font-['Fredoka']" style={darkBg ? { color: '#fff' } : undefined}>{title}</h3>
        <span className="text-xs opacity-60 ml-1" style={darkBg ? { color: '#fff' } : undefined}>({items.length})</span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {items.slice(0, 24).map((a) => (
          <div key={`${a.category}-${a.id}`} className="snap-start shrink-0 w-24">
            <AssetTile asset={a} onClick={onTileClick} onDelete={a._isOwn ? onDelete : null} isOwn={!!a._isOwn} />
          </div>
        ))}
      </div>
    </section>
  );
}
